import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LTTooltip } from '@lambdatestincprivate/lt-components';
import { isInvariant } from '../../model/coverage';
import { readPackBytes } from '../../data/pack-store';
import { truncateMiddle } from '../format';
import { CriteriaIcon, InvariantIcon, ScenarioIcon, SourceGlyph } from './CoverageIcons';
import { SegmentedTabs } from './SegmentedTabs';
import { AcRow } from './AcRow';
import { ScenarioCard } from './ScenarioCard';
import styles from './coverage.module.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var Chevron = function Chevron(_ref) {
  var open = _ref.open;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    style: {
      transform: open ? 'rotate(90deg)' : undefined
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 3.5 11 8l-5 4.5",
      stroke: "currentColor",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};
function statusClass(status) {
  if (status === 'covered') return styles.statusCovered;
  if (status === 'partial') return styles.statusPartial;
  return styles.statusUncovered;
}

/* Figma: bar fill #1f883d green / #9a6700 amber; % text mirrors it. */
function barColor(status) {
  if (status === 'covered') return '#1f883d';
  if (status === 'partial') return '#9a6700';
  return '#cf222e';
}
function pctColor(status) {
  if (status === 'covered') return '#1a7f37';
  if (status === 'partial') return '#9a6700';
  return '#cf222e';
}

/** Download an embedded source blob (pack-relative `file`). Chips whose
 * entry has no `file` render without a download (blob wasn't embeddable). */
function downloadSource(_x, _x2) {
  return _downloadSource.apply(this, arguments);
}
/**
 * One business use-case card. Status pill, % bar and the per-AC score
 * chips all come from the ACTIVE aspect's precomputed rollup — this
 * component computes nothing (rule zero).
 */
function _downloadSource() {
  _downloadSource = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(rel, name) {
    var bytes, url, a;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.next = 1;
          return readPackBytes(rel)["catch"](function () {
            return null;
          });
        case 1:
          bytes = _context.sent;
          if (bytes) {
            _context.next = 2;
            break;
          }
          return _context.abrupt("return");
        case 2:
          url = URL.createObjectURL(new Blob([bytes]));
          a = document.createElement('a');
          a.href = url;
          a.download = name;
          a.click();
          setTimeout(function () {
            return URL.revokeObjectURL(url);
          }, 1000);
        case 3:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _downloadSource.apply(this, arguments);
}
export function UsecaseCard(_ref2) {
  var _uc$title, _uc$title2, _rollup$pct, _rollup$pct2;
  var uc = _ref2.uc,
    aspect = _ref2.aspect,
    focus = _ref2.focus;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    expanded = _useState2[0],
    setExpanded = _useState2[1];
  var _useState3 = useState('scenarios'),
    _useState4 = _slicedToArray(_useState3, 2),
    section = _useState4[0],
    setSection = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    descOpen = _useState6[0],
    setDescOpen = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    flash = _useState8[0],
    setFlash = _useState8[1];
  var rootRef = useRef(null);
  useEffect(function () {
    if (!focus) return undefined;
    setExpanded(true);
    setSection(focus.section);
    setFlash(true);
    // A focused scenario scrolls itself (ScenarioCard) — only scroll the
    // card when the target is the card or its invariants.
    var raf = focus.scenarioId ? null : requestAnimationFrame(function () {
      var _rootRef$current;
      return (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
    var timer = setTimeout(function () {
      return setFlash(false);
    }, 1600);
    return function () {
      if (raf !== null) cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [focus]);
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    descOverflows = _useState0[0],
    setDescOverflows = _useState0[1];
  var descRef = useRef(null);
  var rollup = aspect.usecases[uc.id];
  var staleSet = new Set(uc.staleSources);
  var exercised = new Set(uc.scenarios.flatMap(function (sc) {
    return sc.exercises;
  }));
  // Invariants (use-case-scoped ACs) live in their own segment; the
  // Scenarios segment's fallback group only holds scenario-scoped ACs
  // that no scenario exercises (facts are never hidden either way).
  var invariants = uc.acceptanceCriteria.filter(isInvariant);
  var orphanAcs = uc.acceptanceCriteria.filter(function (a) {
    return !isInvariant(a) && !exercised.has(a.id);
  });

  // View more only when the 2-line clamp actually truncates. Measure in
  // the clamped state (skip while open — clamp is off, nothing to read)
  // and re-measure on width changes.
  useLayoutEffect(function () {
    var el = descRef.current;
    if (descOpen || !el) return undefined;
    var check = function check() {
      return setDescOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    var ro = new ResizeObserver(check);
    ro.observe(el);
    return function () {
      return ro.disconnect();
    };
  }, [uc.description, descOpen]);
  return /*#__PURE__*/_jsxs("div", {
    ref: rootRef,
    className: "".concat(styles.card, " ").concat(flash ? styles.cardFlash : ''),
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.cardHead,
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.cardChevron,
        "aria-expanded": expanded,
        "aria-label": "".concat(expanded ? 'Collapse' : 'Expand', " ").concat((_uc$title = uc.title) !== null && _uc$title !== void 0 ? _uc$title : uc.id),
        onClick: function onClick() {
          return setExpanded(function (v) {
            return !v;
          });
        },
        children: /*#__PURE__*/_jsx(Chevron, {
          open: expanded
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.cardMain,
        children: [/*#__PURE__*/_jsxs("div", {
          className: styles.cardTitleRow,
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.cardTitle,
            children: (_uc$title2 = uc.title) !== null && _uc$title2 !== void 0 ? _uc$title2 : uc.id
          }), (rollup === null || rollup === void 0 ? void 0 : rollup.status) && /*#__PURE__*/_jsx("span", {
            className: "".concat(styles.statusPill, " ").concat(statusClass(rollup.status)),
            children: rollup.status.charAt(0).toUpperCase() + rollup.status.slice(1)
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.metaRow,
          children: [/*#__PURE__*/_jsx(LTTooltip, {
            text: uc.id,
            direction: "bottom",
            children: /*#__PURE__*/_jsx("span", {
              className: styles.idChip,
              children: truncateMiddle(uc.id, 24)
            })
          }), /*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), /*#__PURE__*/_jsxs("span", {
            className: styles.metaItem,
            children: [/*#__PURE__*/_jsx(ScenarioIcon, {}), uc.scenarios.length, " Scenario", uc.scenarios.length === 1 ? '' : 's']
          }), /*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), /*#__PURE__*/_jsxs("span", {
            className: styles.metaItem,
            children: [/*#__PURE__*/_jsx(CriteriaIcon, {}), uc.acceptanceCriteria.length, " Criteria"]
          }), uc.sources.length > 0 && /*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), uc.sources.map(function (src) {
            return src.file ? /*#__PURE__*/_jsxs("button", {
              type: "button",
              className: styles.srcChip,
              title: "Download ".concat(src.id).concat(staleSet.has(src.id) ? " \xB7 stale (changed since derivation)" : ''),
              onClick: function onClick() {
                return void downloadSource(src.file, src.id);
              },
              children: [/*#__PURE__*/_jsx(SourceGlyph, {
                id: src.id,
                mediaType: src.mediaType
              }), src.id]
            }, src.id) : /*#__PURE__*/_jsxs("span", {
              className: "".concat(styles.srcChip, " ").concat(styles.srcChipStatic),
              title: "Source blob not embedded in this pack".concat(staleSet.has(src.id) ? " \xB7 stale (changed since derivation)" : ''),
              children: [/*#__PURE__*/_jsx(SourceGlyph, {
                id: src.id,
                mediaType: src.mediaType
              }), src.id]
            }, src.id);
          })]
        }), uc.description && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            ref: descRef,
            className: "".concat(styles.desc, " ").concat(descOpen ? styles.descOpen : ''),
            children: uc.description
          }), (descOpen || descOverflows) && /*#__PURE__*/_jsx("button", {
            type: "button",
            className: styles.viewMore,
            onClick: function onClick() {
              return setDescOpen(function (v) {
                return !v;
              });
            },
            children: descOpen ? 'View less' : 'View more'
          })]
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.cardRail,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.railBar,
          children: /*#__PURE__*/_jsx("div", {
            className: styles.railFill,
            style: {
              width: "".concat((_rollup$pct = rollup === null || rollup === void 0 ? void 0 : rollup.pct) !== null && _rollup$pct !== void 0 ? _rollup$pct : 0, "%"),
              background: barColor(rollup === null || rollup === void 0 ? void 0 : rollup.status)
            }
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.railLabel,
          children: [/*#__PURE__*/_jsx("span", {
            children: "Coverage"
          }), /*#__PURE__*/_jsxs("span", {
            className: styles.railPct,
            style: {
              color: pctColor(rollup === null || rollup === void 0 ? void 0 : rollup.status)
            },
            children: [(_rollup$pct2 = rollup === null || rollup === void 0 ? void 0 : rollup.pct) !== null && _rollup$pct2 !== void 0 ? _rollup$pct2 : 0, "%"]
          })]
        })]
      })]
    }), expanded && /*#__PURE__*/_jsxs("div", {
      className: styles.expand,
      children: [/*#__PURE__*/_jsx(SegmentedTabs, {
        ariaLabel: "Use-case detail sections",
        value: section,
        onChange: setSection,
        options: [{
          value: 'scenarios',
          label: 'Scenarios',
          icon: /*#__PURE__*/_jsx(ScenarioIcon, {}),
          count: uc.scenarios.length
        }, {
          value: 'invariants',
          label: 'Invariants',
          icon: /*#__PURE__*/_jsx(InvariantIcon, {}),
          count: invariants.length
        }]
      }), section === 'scenarios' && /*#__PURE__*/_jsxs(_Fragment, {
        children: [uc.scenarios.map(function (sc) {
          return /*#__PURE__*/_jsx(ScenarioCard, {
            sc: sc,
            uc: uc,
            aspect: aspect,
            forceOpen: (focus === null || focus === void 0 ? void 0 : focus.scenarioId) === sc.id ? focus.nonce : undefined
          }, sc.id);
        }), orphanAcs.length > 0 && /*#__PURE__*/_jsxs("div", {
          className: styles.scAcGroup,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.scAcHead,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.scAcTitle,
              children: uc.scenarios.length > 0 ? 'Other acceptance criteria' : 'Acceptance Criteria'
            }), /*#__PURE__*/_jsx("span", {
              className: styles.counterBadge,
              children: orphanAcs.length
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.scAcList,
            children: orphanAcs.map(function (ac) {
              return /*#__PURE__*/_jsx(AcRow, {
                ac: ac,
                score: aspect.acs[ac.id]
              }, ac.id);
            })
          })]
        }), uc.scenarios.length === 0 && uc.acceptanceCriteria.length === 0 && /*#__PURE__*/_jsx("span", {
          className: styles.segEmpty,
          children: "No scenarios authored for this use-case."
        })]
      }), section === 'invariants' && (invariants.length > 0 ? /*#__PURE__*/_jsx("div", {
        className: styles.scAcList,
        children: invariants.map(function (ac) {
          return /*#__PURE__*/_jsx(AcRow, {
            ac: ac,
            score: aspect.acs[ac.id],
            usedIn: uc.scenarios.filter(function (sc) {
              return sc.exercises.includes(ac.id);
            })
          }, ac.id);
        })
      }) : /*#__PURE__*/_jsx("span", {
        className: styles.segEmpty,
        children: "No invariants authored for this use-case."
      }))]
    })]
  });
}